import { Button } from "@itandi/itandi-bb-ui/Button";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useState } from "react";
import { DealClientTodoModal } from "~/components/common/deals/show/main/todos/DealClientTodoModal";
import { DealClientTodoTableRow } from "~/components/common/deals/show/main/todos/DealClientTodoTableRow";
import { useDealContext } from "~/context/DealContext";
import { useDealTodos } from "~/hooks/deals/useDealTodos";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useEnabledStaffs } from "~/hooks/useStaffs";
import type { DealTodo } from "~/models/DealTodo";
import { update } from "~/utils/api/deals/DealTodo";
import Styles from "./DealClientTodoTable.module.scss";

export const DealClientTodoTable: React.FC = () => {
	const deal = useDealContext();
	const { data: todos, mutate: mutateTodos } = useDealTodos(deal.id);
	const staffs = useEnabledStaffs().data;
	const [showMode, setShowMode] = useState<"all" | "undone">("undone");
	const [showTodos, setShowTodos] = useState<DealTodo[] | null>(null);
	useEffect(() => {
		if (todos === null) {
			return;
		}
		setShowTodos(
			showMode === "all" ? todos : todos.filter((todo) => !todo.completed)
		);
	}, [todos, showMode]);

	const handleCheck = useCallback(
		(newTodo: DealTodo): void => {
			if (showTodos === null) return;
			update(deal.id, newTodo.id, newTodo).then();
			const targetIndex = showTodos.findIndex(
				(t: DealTodo) => t.id === newTodo.id
			);
			const newTodos = [...showTodos];
			newTodos.splice(targetIndex, 1, newTodo);
			setShowTodos(newTodos);
		},
		[deal.id, showTodos]
	);

	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const renderRow = React.useCallback(
		(todo: DealTodo): ReactNode => (
			<DealClientTodoTableRow
				handleCheck={handleCheck}
				key={todo.id}
				todo={todo}
			/>
		),
		[handleCheck]
	);
	if (todos === null || staffs === null) {
		return null;
	}
	return (
		<div>
			<div className={Styles.RadioGroup}>
				<InputRadio
					checked={showMode === "undone"}
					label="未完了のみ表示"
					name="showMode"
					onChange={async (): Promise<void> => {
						await mutateTodos();
						setShowMode("undone");
					}}
					value="undone"
				/>
				<InputRadio
					checked={showMode === "all"}
					label="すべて表示"
					name="showMode"
					onChange={async (): Promise<void> => {
						await mutateTodos();
						setShowMode("all");
					}}
					value="all"
				/>
			</div>
			<Table>
				<Thead>
					<Tr>
						<Th width="3rem">完了</Th>
						<Th>タイトル</Th>
						<Th>担当者</Th>
						<Th>対応期限</Th>
						<Th>完了日</Th>
						<Th>操作</Th>
					</Tr>
				</Thead>
				<Tbody>{showTodos !== null && showTodos.map(renderRow)}</Tbody>
			</Table>
			{showTodos !== null && showTodos.length === 0 ? (
				<div className={Styles.EmptyPanel}>
					<p className={Styles.EmptyMessage}>TODOはありません</p>
					<Button height="small" onClick={handleOpen} variant="tertiary">
						TODOを追加
					</Button>
				</div>
			) : (
				<div className={Styles.AddButtonArea}>
					<Button height="small" onClick={handleOpen} variant="tertiary">
						TODOを追加
					</Button>
				</div>
			)}
			{isOpen ? (
				<DealClientTodoModal
					deal={deal}
					handleClose={handleClose}
					staffs={staffs}
				/>
			) : null}
		</div>
	);
};
